import { FC, Suspense, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Asset, THOR } from "rujira.js";
import {
  AssetOverviewModal,
  AssetOverviewOption,
  Icons,
  sortByValueUsd,
  useTranslation,
} from "rujira.ui";
import {
  BalanceProvider,
  usePreloadedBalances,
} from "../../../common/components/Balance";
import { isSunsetSymbol } from "../../../services/asset";
import { isSameCanonicalAsset } from "../../../services/canonicalAsset";
import { TransferSession } from "../../../common/components/TransferSession";
import { findAssetByUrlSegment } from "../../../services/assetUrl";
import { AssetFlowQuery } from "./__generated__/AssetFlowQuery.graphql";
import { Send } from "./Send";
import { Withdraw } from "./Withdraw";

export type AssetFlowAction = "send" | "withdraw";

const query = graphql`
  query AssetFlowQuery {
    thorchainV2 {
      pools {
        status
        asset {
          chain
          metadata {
            symbol
          }
        }
      }
    }
  }
`;

export const AssetFlow: FC<{
  action: AssetFlowAction;
  close: () => void;
  assetSegment?: string;
}> = ({ action, close, assetSegment }) => (
  <Suspense fallback={<AssetFlowFallback />}>
    <AssetFlowContent
      action={action}
      close={close}
      assetSegment={assetSegment}
    />
  </Suspense>
);

const AssetFlowContent: FC<{
  action: AssetFlowAction;
  close: () => void;
  assetSegment?: string;
}> = ({ action, close, assetSegment }) => {
  const { t } = useTranslation("common");
  const balances = usePreloadedBalances();
  const data = useLazyLoadQuery<AssetFlowQuery>(query, {});

  const options = useMemo(
    () =>
      sortByValueUsd(
        balances.flatMap((balance): AssetOverviewOption[] => {
          if (isSunsetSymbol(balance.asset.metadata.symbol)) return [];

          const accounts = balance.accounts.filter((account) =>
            action === "withdraw"
              ? account.asset.type === "SECURED" && account.balance > 0n
              : account.balance > 0n
          );
          if (!accounts.length) return [];

          const networks =
            action === "withdraw"
              ? [
                  ...new Set(
                    (data.thorchainV2?.pools || [])
                      .filter(
                        (pool) =>
                          pool.status === "AVAILABLE" &&
                          pool.asset.chain !== THOR &&
                          isSameCanonicalAsset(
                            pool.asset.metadata.symbol,
                            balance.asset.metadata.symbol
                          )
                      )
                      .map((pool) => pool.asset.chain)
                  ),
                ]
              : [
                  ...new Set(
                    accounts.map((account) =>
                      account.asset.type === "SECURED"
                        ? THOR
                        : account.asset.chain
                    )
                  ),
                ];

          return [
            {
              asset: balance.asset,
              amount: accounts.reduce(
                (total, account) => total + account.balance,
                0n
              ),
              valueUsd: accounts.reduce(
                (total, account) => total + account.valueUsd,
                0n
              ),
              networks,
            },
          ];
        })
      ),
    [action, balances, data.thorchainV2?.pools]
  );
  // `undefined` = the user has not chosen yet, so follow the URL segment (which
  // may only resolve once balances load); `null` = they went back to the list.
  const [picked, setPicked] = useState<Asset | null>();
  const fromSegment = useMemo(
    () =>
      assetSegment
        ? findAssetByUrlSegment(
            assetSegment,
            options.map((option) => option.asset)
          )
        : undefined,
    [assetSegment, options]
  );
  const selected = picked === undefined ? fromSegment : (picked ?? undefined);

  if (selected) {
    return (
      <BalanceProvider asset={selected}>
        <TransferSession>
          {(connect) => (
            <>
              <button
                type="button"
                className="transfer-modal__back transparent color-white hover-primary1 flex ai-c mb-1 fs-14 fw-500 pointer"
                onClick={() => setPicked(null)}>
                <Icons.AngleLeft className="block w-2 h-2 mr-0.5" />
                {t("back")}
              </button>
              {action === "withdraw" ? (
                <Withdraw cancel={close} connect={connect} />
              ) : (
                <Send cancel={close} connect={connect} />
              )}
            </>
          )}
        </TransferSession>
      </BalanceProvider>
    );
  }

  return (
    <AssetOverviewModal
      title={
        action === "withdraw" ? t("withdrawAssetsTitle") : t("sendAssetsTitle")
      }
      description={
        action === "withdraw"
          ? t("withdrawAssetsDescription")
          : t("sendAssetsDescription")
      }
      searchPlaceholder={t("searchAssets")}
      emptyLabel={t("noBalancesFound")}
      options={options}
      onSelect={setPicked}
    />
  );
};

const AssetFlowFallback: FC = () => (
  <div className="asset-overview-modal">
    <div className="skeleton h-5 w-24 br-2" />
    <div className="skeleton h-2 w-full br-2 mt-2" />
    <div className="skeleton h-6 w-full br-1 mt-3" />
    <div className="skeleton h-8 w-full br-2 mt-2" />
  </div>
);
